import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { CalendarClock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import type { ServiceType } from "@/lib/services";
import { VISIT_TIME_SLOTS, createVisitRequest, todayISO } from "@/lib/visits";

const schema = z.object({
  name: z.string().trim().min(2, "Enter your name").max(80, "Name is too long"),
  mobile: z
    .string()
    .trim()
    .min(7, "Enter a valid mobile number")
    .max(20, "Mobile number is too long")
    .regex(/^[0-9+\-\s()]+$/, "Mobile can only contain digits and + - ( )"),
  date: z
    .string()
    .min(1, "Choose a preferred date")
    .refine((d) => d >= todayISO(), "Preferred date cannot be in the past"),
  time: z.string().min(1, "Choose a preferred time"),
  message: z.string().trim().max(600, "Message is too long").optional(),
});

export interface VisitTarget {
  id: string;
  title: string;
  serviceType: ServiceType;
  partnerId: string | null;
  demo?: boolean;
}

export function VisitRequestForm({ target, onDone }: { target: VisitTarget; onDone: () => void }) {
  const { user, profile } = useAuth();
  const p = profile as { full_name?: string | null; phone?: string | null } | null;
  const [name, setName] = useState(p?.full_name ?? "");
  const [mobile, setMobile] = useState(p?.phone ?? "");
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState<string>(VISIT_TIME_SLOTS[1]);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const parsed = schema.safeParse({ name, mobile, date, time, message });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] = issue.message;
      setErrors(next);
      return;
    }
    setErrors({});
    if (!user) return;
    setBusy(true);
    try {
      if (target.demo || !target.partnerId) {
        toast.success("Visit request sent. The partner will confirm shortly.");
        onDone();
        return;
      }
      await createVisitRequest({
        listingId: target.id,
        serviceType: target.serviceType,
        listingTitle: target.title,
        partnerId: target.partnerId,
        customerId: user.id,
        customerName: name,
        customerContact: mobile,
        preferredDate: date,
        preferredTime: time,
        message,
      });
      toast.success("Visit request sent. You'll be notified when the partner responds.");
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send the visit request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-3">
      <Field label="Your name" error={errors.name}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          placeholder="Full name"
        />
      </Field>
      <Field label="Mobile number" error={errors.mobile}>
        <Input
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          maxLength={20}
          placeholder="Mobile number"
          inputMode="tel"
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Preferred date" error={errors.date}>
          <Input
            type="date"
            min={todayISO()}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field label="Preferred time" error={errors.time}>
          <select
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-brown-dark"
          >
            {VISIT_TIME_SLOTS.map((slot) => (
              <option key={slot} value={slot}>
                {slot}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Message (optional)" error={errors.message}>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={600}
          rows={3}
          placeholder="Anything the partner should know before the visit?"
        />
      </Field>

      <button
        type="button"
        onClick={submit}
        disabled={busy}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brown-dark px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
      >
        <CalendarClock className="h-4 w-4" />
        {busy ? "Sending…" : "Request visit"}
      </button>
      <p className="text-center text-[11px] text-muted-foreground">
        The partner will accept, reschedule or cancel your request. You'll get a notification for
        every update.
      </p>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-brown-dark">{label}</span>
      {children}
      {error && <span className="mt-1 block text-[11px] text-coral">{error}</span>}
    </label>
  );
}
